'use client';

import { memo, type ReactNode, useCallback, useMemo } from 'react';

import { ClipType } from '@air/api/types';
import { BoardIcon, ImageIcon, SquarePlayIcon } from '@air/design-icons';
import { DropdownMenu, DropdownMenuItem } from '@air/primitive-dropdown-menu';
import { cn } from '@air/utils-tailwind';

import { type GeneralLibraryColor } from '~/components/tree/GeneralLibraryTreeItem';

export type BreadcrumbItem = {
  id: string;
  title: string;
};

export type BreadcrumbsProps = {
  /** Library icon color */
  libraryColor?: GeneralLibraryColor;
  /** Library icon emoji */
  libraryIcon?: string;
  /** Library name */
  libraryName?: string;
  /** Library ID for click handler */
  libraryId?: string;
  /** Ancestor boards (from root to parent of current) */
  ancestors?: BreadcrumbItem[];
  /** Current board name */
  currentBoardName?: string;
  /** Current board ID for click handler (required when assetName is set) */
  currentBoardId?: string;
  /** Handler for library click */
  onLibraryClick?: (libraryId: string) => void;
  /** Handler for board click */
  onBoardClick?: (boardId: string) => void;
  /** Maximum number of items to show before truncating (excluding library) */
  maxVisibleItems?: number;
  /** Show loading skeleton */
  isLoading?: boolean;
  /** Asset name to display (when viewing asset detail) */
  assetName?: string;
  /** Asset type for icon display */
  assetType?: ClipType;
};

export type LibraryIconProps = {
  color?: GeneralLibraryColor;
  icon?: string;
};

/** Colored library icon */
const LibraryIcon = memo(({ color, icon }: LibraryIconProps) => {
  const style = useMemo(
    () => ({
      backgroundColor: color?.backgroundHex ?? '#dcdcdc',
      color: color?.fontHex ?? '#333333',
    }),
    [color?.backgroundHex, color?.fontHex],
  );

  if (!icon) {
    return null;
  }

  return (
    <div className="flex size-5 shrink-0 items-center justify-center rounded text-10" style={style}>
      {icon}
    </div>
  );
});

LibraryIcon.displayName = 'LibraryIcon';

/** Separator between breadcrumb items */
const BreadcrumbSeparator = memo(() => <span className="text-neutral-9">/</span>);

BreadcrumbSeparator.displayName = 'BreadcrumbSeparator';

export type BreadcrumbButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  /** When true, renders as non-interactive text instead of a button */
  disabled?: boolean;
  /** Whether this item can shrink when space is limited */
  canShrink?: boolean;
};

/** Clickable breadcrumb item */
const BreadcrumbButton = memo(({ children, onClick, disabled, canShrink = false }: BreadcrumbButtonProps) => {
  if (disabled || !onClick) {
    return (
      <span
        className={cn('flex items-center gap-1.5 px-2 font-normal text-neutral-12', {
          'min-w-0 shrink truncate': canShrink,
          'shrink-0': !canShrink,
        })}
      >
        {children}
      </span>
    );
  }

  return (
    <button
      className={cn(
        'flex items-center gap-1.5 rounded px-2 py-1 text-neutral-12 transition-colors hover:bg-neutral-3',
        { 'min-w-0 shrink truncate': canShrink, 'shrink-0': !canShrink },
      )}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
});

BreadcrumbButton.displayName = 'BreadcrumbButton';

/** Dropdown for hidden breadcrumb items */
export type EllipsisDropdownProps = {
  hiddenItems: BreadcrumbItem[];
  onItemClick: (boardId: string) => void;
};

const EllipsisDropdown = memo(({ hiddenItems, onItemClick }: EllipsisDropdownProps) => {
  return (
    <DropdownMenu
      align="start"
      sideOffset={4}
      trigger={
        <button
          aria-label="Show hidden breadcrumbs"
          className="rounded px-2 py-1 text-neutral-11 transition-colors hover:bg-neutral-3"
          type="button"
        >
          ...
        </button>
      }
    >
      {hiddenItems.map((item) => (
        // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
        <DropdownMenuItem key={item.id} label={item.title} onSelect={() => onItemClick(item.id)} />
      ))}
    </DropdownMenu>
  );
});

EllipsisDropdown.displayName = 'EllipsisDropdown';

/** Skeleton placeholder for board path while loading */
const BoardPathSkeleton = memo(() => (
  <>
    <BreadcrumbSeparator />
    <div className="h-4 w-24 animate-pulse rounded bg-neutral-4" />
  </>
));

BoardPathSkeleton.displayName = 'BoardPathSkeleton';

/** Full skeleton placeholder when library is also loading */
const BreadcrumbsSkeleton = memo(() => (
  <div className="flex items-center gap-1.5 font-medium text-12">
    <div className="size-5 animate-pulse rounded bg-neutral-4" />
    <div className="h-4 w-20 animate-pulse rounded bg-neutral-4" />
  </div>
));

BreadcrumbsSkeleton.displayName = 'BreadcrumbsSkeleton';

export const Breadcrumbs = memo(
  ({
    libraryColor,
    libraryIcon,
    libraryName,
    libraryId,
    ancestors,
    currentBoardName,
    currentBoardId,
    onLibraryClick,
    onBoardClick,
    maxVisibleItems = 2,
    isLoading,
    assetName,
    assetType,
  }: BreadcrumbsProps) => {
    // Build the list of items to display (excluding library which is always shown)
    const boardItems = useMemo(() => {
      const items: BreadcrumbItem[] = [...(ancestors ?? [])];

      if (currentBoardName) {
        // Use actual board ID when available (needed for clickable breadcrumb when viewing asset)
        items.push({ id: currentBoardId ?? 'current', title: currentBoardName });
      }

      return items;
    }, [ancestors, currentBoardId, currentBoardName]);

    // Determine which items to show (truncate middle if needed)
    const displayData = useMemo(() => {
      if (boardItems.length <= maxVisibleItems) {
        return { hiddenItems: [], showEllipsis: false, visibleItems: boardItems };
      }

      // Show last N items, with ellipsis before them
      const hiddenItems = boardItems.slice(0, -maxVisibleItems);
      const visibleItems = boardItems.slice(-maxVisibleItems);

      return {
        hiddenItems,
        showEllipsis: true,
        visibleItems,
      };
    }, [boardItems, maxVisibleItems]);

    const handleLibraryClick = useCallback(() => {
      if (libraryId && onLibraryClick) {
        onLibraryClick(libraryId);
      }
    }, [libraryId, onLibraryClick]);

    const handleBoardClick = useCallback(
      (boardId: string) => {
        if (onBoardClick) {
          onBoardClick(boardId);
        }
      },
      [onBoardClick],
    );

    // Get the first board item for the "root" breadcrumb when no library
    const firstBoardItem = useMemo(
      () => (!libraryName && boardItems.length > 0 ? boardItems[0] : null),
      [libraryName, boardItems],
    );

    const handleFirstBoardClick = useCallback(() => {
      if (firstBoardItem && firstBoardItem.id !== 'current') {
        handleBoardClick(firstBoardItem.id);
      }
    }, [firstBoardItem, handleBoardClick]);

    // Compute visible/hidden items for ellipsis (when no library, we need to recalculate)
    const { hiddenItemsForEllipsis, showEllipsisAfterFirst, visibleItemsAfterFirst } = useMemo(() => {
      if (libraryName) {
        return {
          hiddenItemsForEllipsis: displayData.hiddenItems,
          showEllipsisAfterFirst: displayData.showEllipsis,
          visibleItemsAfterFirst: displayData.visibleItems,
        };
      }

      if (boardItems.length > maxVisibleItems + 1) {
        const hidden = boardItems.slice(1, -maxVisibleItems);

        return {
          hiddenItemsForEllipsis: hidden,
          showEllipsisAfterFirst: hidden.length > 0,
          visibleItemsAfterFirst: boardItems.slice(-maxVisibleItems),
        };
      }

      return {
        hiddenItemsForEllipsis: [] satisfies BreadcrumbItem[],
        showEllipsisAfterFirst: false,
        visibleItemsAfterFirst: boardItems.slice(1),
      };
    }, [libraryName, boardItems, maxVisibleItems, displayData]);

    // Show full skeleton when library is still loading
    if (isLoading && !libraryName) {
      return <BreadcrumbsSkeleton />;
    }

    return (
      <div className="flex min-w-0 flex-1 items-center gap-1.5 font-medium text-12">
        {libraryName && (
          <BreadcrumbButton
            canShrink={!!assetName}
            disabled={!boardItems.length && !isLoading}
            onClick={libraryId ? handleLibraryClick : undefined}
          >
            <LibraryIcon color={libraryColor} icon={libraryIcon} />
            {libraryName}
          </BreadcrumbButton>
        )}
        {!libraryName && firstBoardItem && (
          <BreadcrumbButton
            canShrink={!!assetName}
            disabled={boardItems.length === 1 && !assetName}
            onClick={handleFirstBoardClick}
          >
            <BoardIcon className="shrink-0 text-neutral-10" size="sm" />
            {firstBoardItem.title}
          </BreadcrumbButton>
        )}

        {isLoading ? (
          <BoardPathSkeleton />
        ) : (
          <>
            {showEllipsisAfterFirst && (
              <>
                <BreadcrumbSeparator />
                <EllipsisDropdown hiddenItems={hiddenItemsForEllipsis} onItemClick={handleBoardClick} />
              </>
            )}

            {visibleItemsAfterFirst.map((item, index) => {
              const isLastItem = index === visibleItemsAfterFirst.length - 1 && !!currentBoardName;
              // When viewing an asset, the last board is clickable (to close the asset modal)
              const disabled = isLastItem && !assetName;

              return (
                <span className="flex items-center gap-1.5" key={item.id}>
                  <BreadcrumbSeparator />
                  <BreadcrumbButton
                    canShrink={!isLastItem || !!assetName}
                    disabled={disabled}
                    // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                    onClick={() => handleBoardClick(item.id)}
                  >
                    {item.title}
                  </BreadcrumbButton>
                </span>
              );
            })}

            {/* Asset breadcrumb (when viewing asset detail) */}
            {assetName && (
              <span className="flex min-w-0 flex-1 items-center gap-1.5">
                <BreadcrumbSeparator />
                <span className="flex min-w-0 flex-1 items-center gap-1.5 px-2 font-normal text-neutral-12">
                  {assetType === ClipType.video ? (
                    <SquarePlayIcon className="shrink-0 text-neutral-10" size="sm" />
                  ) : (
                    <ImageIcon className="shrink-0 text-neutral-10" size="sm" />
                  )}
                  <span className="truncate">{assetName}</span>
                </span>
              </span>
            )}
          </>
        )}
      </div>
    );
  },
);

Breadcrumbs.displayName = 'Breadcrumbs';
